<template>
	<div class="app">
		<app-bar 
			:title="brand.title" 
			:logo="brand.logo.light" 
			@draweropen="openDrawer"
			@logoclick="onLogoClick"
		>
            <desktop-app-navigation />

			<app-bar-identity :user-name="userFullName" :user-title="userEmail">
				<list>
					<list-item
						title="Sign out"
						primary-icon="exit_to_app"
						clickable
						@click="logout"
					/>
				</list>
			</app-bar-identity>
		</app-bar>

		<app-content>
			
			<custom-app-drawer 
				:user-name="userFullName"
                :user-title="userEmail" 
				:open="drawerOpen"
				@open="openDrawer"
				@close="closeDrawer"
			>
                <list slot="main-nav">
                    <list-item
                        v-for="group in locationGroups"
                        :key="group.locationGroupID"
                        :title="group.reportName"
                        :to="{ name: 'group-dashboards', params: { locationGroupId: group.locationGroupID } }"
                        :active="group.locationGroupID === activeDashboardLocationGroupID"
                    >
                        <group-avatar
                            slot="primary-object"
                            :name="group.reportName"
                            :src="group.iconImage"
                            :value="group.iconText"
                            :color="group.iconTextColor"
                            :background-color="group.iconBackgroundColor"
                            :size="24"
                        />
                    </list-item>

                    <list-item
                        v-if="!locationGroups || !locationGroups.length"
                        title="Dashboards"
                        primary-icon="dashboard"
                        :to="{ name: 'dashboards' }"
                    />

                    <list-header v-if="isGroupAdmin">Administration</list-header>

                    <list-item
                        v-if="isGroupAdmin"
                        title="Groups"
                        primary-icon="group_work"
                        :to="{ name: 'group-list' }"
                        :active="isCurrentRoutePath('groups')"
                    />
                    <list-item
                        v-if="isGroupAdmin"
                        title="Users"
                        primary-icon="supervisor_account"
                        :to="{ name: 'user-list' }"
                        :active="isCurrentRoutePath('users')"
                    />
                </list>

                <list slot="footer">
                   <list-item
                        title="Sign out"
                        primary-icon="exit_to_app"
                        @click="logout"
                    />
                </list>
			</custom-app-drawer>

			<app-body />

            <app-footer />

		</app-content>

        <idle-timeout-modal ref="idleTimeoutModal" @renew="onRenew" @logout="logout" />

        <app-update-required-modal />
	</div>
</template>


<script>
    import _ from 'lodash';
    import store from 'store';
    import { browser } from 'pdk/services';
    import { AsyncMixin } from 'pdk/mixins';
    import LetterAvatarHelper from 'pdk/components/letter-avatar-helper';
	import AppConfig from '@/config';
    import { IdleTimeoutModal } from '@/pages/auth/modals';
    import { AppUpdateRequiredModal } from '@/pages/app/modals';
    import { getters, actions } from '@/store';
    import { getters as dashboardGetters, actions as dashboardStoreActions } from 'dashboard-pdk/store';
    import { GroupAvatar } from '@/components/groups';
    import { CustomAppDrawer, DesktopAppNavigation, AppFooter } from '@/components/app';


	export default {
		name: 'portal-layout',
        components: {
            CustomAppDrawer,
            DesktopAppNavigation,
            AppFooter,
            GroupAvatar,
            IdleTimeoutModal,
            AppUpdateRequiredModal
        },
        mixins: [AsyncMixin],
        beforeRouteEnter(to, __, next) {
            next((vm) => {
                vm.updateActiveDashboardLocationGroupID(to);
            });
        },
        beforeRouteUpdate(to, __, next) {
            this.updateActiveDashboardLocationGroupID(to);

            next();
        },
		data() {
			return {
                brand: AppConfig.brand,
                idleTimeoutHandler: undefined,
                activeDashboardLocationGroupID: undefined,
                drawerCollapsed: false,
                avatarHelper: LetterAvatarHelper,
                mobile: browser.getBrowser().mobile
			};
		},
		computed: {
			userFullName() {
                const first = this.$auth.tokenParsed.given_name;
                const last = this.$auth.tokenParsed.family_name;
                if (!first && !last) return undefined;

                return _.compact([first, last]).join(' ');
            },
            userEmail() {
				return this.$auth.tokenParsed.email;
            },
			drawerOpen() {
				return this.$store.state.app.drawerOpen;
            },
            activeLocationGroup() {
                return this.$store.getters[dashboardGetters.ACTIVE_LOCATION_GROUP];
            },
            locationGroups() {
                return this.$store.state.dashboard.locationGroups;
            },
            locationGroupNames() {
                if (!this.locationGroups || !this.locationGroups.length) return [];
                return _.map(this.locationGroups, (g) => g.reportName);
            },
			isGroupAdmin() {
                return this.$store.getters[getters.USER_GROUPS_IS_ADMIN];
            }
		},
        created() {
            const drawerCollapsedPreference = store.get('.menu-collapse');
            this.drawerCollapsed = typeof drawerCollapsedPreference !== 'undefined'
                ? !!drawerCollapsedPreference
                : true;

			this.$router.afterEach(() => {
				this.closeDrawer();
            });
        },
        methods: {
            updateActiveDashboardLocationGroupID(route) {
                    this.activeDashboardLocationGroupID = (route.name === 'group-dashboards')
                    ? route.params.locationGroupId
                    : undefined;
            },
			onLogoClick() {
                this.$router.push({ name: 'home' });
            },
            onDrawerCollapse(collapsed) {
                this.drawerCollapsed = collapsed;
                store.set('.menu-collapse', collapsed);
            },
			openDrawer() {
				this.$store.dispatch(actions.APP_DRAWER_OPEN);
			},
			closeDrawer() {
				this.$store.dispatch(actions.APP_DRAWER_CLOSE);
			},
			logout() {
                this.$store.dispatch(dashboardStoreActions.CLEAR);
                this.$store.dispatch(actions.LOGOUT);
            },
            isCurrentRoutePath(path) {
                return this.$route.path.substring(1).toLowerCase().startsWith(path.toLowerCase());
            },
            
            onRenew() {
                this.$auth.updateToken(-1)
                    .then((refreshed) => {
                        if (!refreshed)
                            this.$auth.clearToken();
                    }).catch(() => {
                        this.$auth.clearToken();
                    });
            }
		}
	};
</script>
